import React from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { useBudgetReport } from '@/services/budgetService';
import { formatCurrency } from '@/utils/formatters';
import { BudgetMeter } from './BudgetMeter';

const TOP = 5;

/**
 * Dashboard card for department budgets (#19): the current fiscal year's total budget, committed
 * and remaining, and the departments with the highest share of their budget committed. Lives in
 * its own component so Dashboard.tsx only gains one element.
 */
export const BudgetSummaryCard: React.FC = () => {
  const { data: report, isLoading, error } = useBudgetReport();
  const budgeted = (report?.rows ?? [])
    .filter((row) => row.departmentId && row.committedPercent !== undefined)
    .sort((a, b) => (b.committedPercent ?? 0) - (a.committedPercent ?? 0))
    .slice(0, TOP);
  const totals = report?.totals;

  return (
    <Card className="mb-8" data-testid="budget-summary-card">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Department Budgets</CardTitle>
            <CardDescription>
              Committed against budget{report ? `, fiscal year ${report.fiscalYearLabel}` : ''}
            </CardDescription>
          </div>
          <Link
            to="/budgets"
            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline whitespace-nowrap"
          >
            Open budgets
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-[160px] w-full" />
        ) : error ? (
          <Alert variant="destructive">
            <AlertDescription>Failed to load budgets: {error.message}</AlertDescription>
          </Alert>
        ) : report && totals ? (
          <>
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
              <div>
                <dt className="text-xs text-grey-500">Total budget</dt>
                <dd className="text-xl font-semibold text-grey-800">
                  {totals.budget !== undefined ? formatCurrency(totals.budget) : 'No budgets set'}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-grey-500">Committed</dt>
                <dd className="text-xl font-semibold text-grey-800">{formatCurrency(totals.committed)}</dd>
              </div>
              <div>
                <dt className="text-xs text-grey-500">Remaining</dt>
                <dd className={cn('text-xl font-semibold', (totals.remaining ?? 0) < 0 ? 'text-destructive' : 'text-grey-800')}>
                  {totals.remaining !== undefined ? formatCurrency(totals.remaining) : '—'}
                </dd>
              </div>
            </dl>
            {budgeted.length > 0 ? (
              <ul className="space-y-3">
                {budgeted.map((row) => (
                  <li key={row.departmentId} className="grid grid-cols-[minmax(0,1fr)_minmax(120px,2fr)] items-center gap-3">
                    <span className="text-sm text-grey-700 break-words">
                      {row.departmentName}
                      <span className="block text-xs text-grey-500">
                        {formatCurrency(row.committed)} of {formatCurrency(row.budget ?? 0)}
                      </span>
                    </span>
                    <BudgetMeter percent={row.committedPercent} />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-grey-500">No department has a budget for this fiscal year yet.</p>
            )}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};
